import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import LegalMarkdown from '../components/LegalMarkdown';
import LegalVersionDiff from '../components/LegalVersionDiff';
import { Scale, Save, Eye, History, Loader2, RotateCcw, ExternalLink, GitCompare, Users, BellRing } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DOCS = [
  { slug: 'privacy', label: 'Privacy Policy', path: '/privacy-policy' },
  { slug: 'terms', label: 'Terms of Service', path: '/terms' },
];

const VersionHistory = ({ slug, versions, onRestore, onCompare, busy }) => (
  <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="legal-version-history">
    <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}><History className="w-4 h-4 text-[#002FA7]" /> Previous versions</h3>
    {versions.length === 0 && <p className="text-sm text-slate-500" data-testid="legal-no-versions">No earlier versions yet — the first publish will archive the current text here.</p>}
    <ul className="divide-y divide-slate-100">
      {versions.map((v) => (
        <li key={v.version} className="py-2 flex flex-wrap items-center gap-3 text-sm" data-testid={`legal-version-${v.version}`}>
          <span className="font-semibold text-slate-800">v{v.version}</span>
          <span className="text-slate-500">effective {v.effective_date} · published {v.updated_at ? new Date(v.updated_at).toLocaleString() : '—'} by {v.updated_by_email || '—'}</span>
          {v.change_note && <span className="text-slate-600 italic">“{v.change_note}”</span>}
          <button type="button" onClick={() => onCompare(v)} data-testid={`legal-compare-${v.version}`}
            className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded border border-[#002FA7]/40 text-xs font-semibold text-[#002FA7] hover:bg-[#002FA7]/5">
            <GitCompare className="w-3 h-3" /> Compare with live
          </button>
          <button type="button" disabled={!!busy} onClick={() => onRestore(v.version)} data-testid={`legal-restore-${v.version}`}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-slate-300 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            <RotateCcw className="w-3 h-3" /> Restore
          </button>
        </li>
      ))}
    </ul>
  </div>
);

const AcceptanceStats = ({ stats, onRemind, busy }) => {
  if (!stats) return null;
  const pct = stats.total_users ? Math.round((stats.accepted_current / stats.total_users) * 100) : 0;
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="legal-acceptance-stats">
      <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}><Users className="w-4 h-4 text-[#002FA7]" /> Acceptance tracking</h3>
      <div className="grid grid-cols-3 gap-3 mb-3 text-center">
        <div className="bg-slate-50 rounded-md p-3"><div className="text-2xl font-bold text-slate-900" data-testid="legal-acceptance-current">{stats.accepted_current}</div><div className="text-[11px] uppercase tracking-widest text-slate-500">Accepted v{stats.current_version}</div></div>
        <div className="bg-slate-50 rounded-md p-3"><div className="text-2xl font-bold text-amber-700" data-testid="legal-acceptance-pending">{stats.pending}</div><div className="text-[11px] uppercase tracking-widest text-slate-500">Pending</div></div>
        <div className="bg-slate-50 rounded-md p-3"><div className="text-2xl font-bold text-slate-900">{pct}%</div><div className="text-[11px] uppercase tracking-widest text-slate-500">of {stats.total_users} users</div></div>
      </div>
      <p className="text-xs text-slate-500 mb-3">Users are asked to accept on their next sign-in after every publish. Each acceptance stores user, version, time, IP and device.</p>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-amber-50 border border-amber-200 rounded-md p-3 mb-3" data-testid="legal-reminder-box">
        <div className="flex-1 text-xs text-amber-900">
          <span className="font-semibold">Pending-acceptance reminders:</span> users still pending {stats.reminder_after_days} days after a publish are e-mailed automatically (once per version).
          <span className="ml-1" data-testid="legal-reminded-count">{stats.reminded_current} reminded for v{stats.current_version}.</span>
        </div>
        <button type="button" onClick={onRemind} disabled={!!busy || stats.pending === 0} data-testid="legal-remind-now"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-50 shrink-0">
          {busy === 'remind' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />} Send reminders now
        </button>
      </div>
      {stats.recent.length > 0 && (
        <div className="max-h-56 overflow-auto border border-slate-100 rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="px-2 py-1 font-semibold">User</th><th className="px-2 py-1 font-semibold">Role</th><th className="px-2 py-1 font-semibold">Version</th><th className="px-2 py-1 font-semibold">Accepted</th></tr></thead>
            <tbody>
              {stats.recent.map((r, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid="legal-acceptance-row"><td className="px-2 py-1 text-slate-800">{r.user_email}</td><td className="px-2 py-1 text-slate-500">{r.user_role}</td><td className="px-2 py-1">v{r.version}</td><td className="px-2 py-1 text-slate-500">{new Date(r.accepted_at).toLocaleString()}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SuperAdminLegal = () => {
  const [slug, setSlug] = useState('privacy');
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({ title: '', body: '', effective_date: '', change_note: '' });
  const [versions, setVersions] = useState([]);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState('');
  const [compare, setCompare] = useState(null);
  const [acceptances, setAcceptances] = useState(null);

  const load = useCallback(async () => {
    setDoc(null);
    setForm({ title: '', body: '', effective_date: '', change_note: '' });
    try {
      const [d, v, a] = await Promise.all([
        axios.get(`${API}/legal/${slug}`),
        axios.get(`${API}/legal/${slug}/versions`, { withCredentials: true }),
        axios.get(`${API}/legal/${slug}/acceptances`, { withCredentials: true }),
      ]);
      if (d.data.slug !== slug) return; // stale response after a fast tab switch
      setDoc(d.data);
      setForm({ title: d.data.title, body: d.data.body, effective_date: d.data.effective_date, change_note: '' });
      setVersions(v.data);
      setAcceptances(a.data);
      setCompare(null);
    } catch { toast.error('Could not load document'); }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const dirty = doc && (form.body !== doc.body || form.title !== doc.title || form.effective_date !== doc.effective_date);

  const publish = async () => {
    if (!doc || doc.slug !== slug) return;
    setBusy('publish');
    try {
      await axios.put(`${API}/legal/${doc.slug}`, form, { withCredentials: true });
      toast.success(`${form.title} published — live now at ${DOCS.find((d) => d.slug === slug).path}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Publish failed'); }
    finally { setBusy(''); }
  };

  const restore = async (version) => {
    if (!window.confirm(`Republish version ${version}? The current text is archived first.`)) return;
    setBusy('restore');
    try {
      await axios.post(`${API}/legal/${slug}/restore/${version}`, {}, { withCredentials: true });
      toast.success(`Version ${version} restored`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); }
    finally { setBusy(''); }
  };

  const remind = async () => {
    setBusy('remind');
    try {
      const { data } = await axios.post(`${API}/legal/${slug}/remind-pending`, {}, { withCredentials: true });
      toast.success(data.sent_now ? `${data.sent_now} reminder e-mail${data.sent_now === 1 ? '' : 's'} sent` : 'Everyone pending was already reminded for this version');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send reminders'); }
    finally { setBusy(''); }
  };

  const current = DOCS.find((d) => d.slug === slug);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="legal-editor-page" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
        <header className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Work Sans, sans-serif' }}><Scale className="w-8 h-8 text-[#002FA7]" /> Legal Documents</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">Counsel edits ship instantly — no redeploy. Formatting: <code>## Heading</code>, blank line between paragraphs, <code>- bullet</code>, <code>**bold**</code>, <code>*italic*</code>, <code>[link](https://…)</code>. Every publish archives the previous version.</p>
        </header>

        <div className="flex flex-wrap items-center gap-2 mb-5" role="tablist">
          {DOCS.map((d) => (
            <button key={d.slug} type="button" role="tab" aria-selected={slug === d.slug} onClick={() => setSlug(d.slug)} data-testid={`legal-tab-${d.slug}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold ${slug === d.slug ? 'bg-[#002FA7] text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'}`}>
              {d.label}
            </button>
          ))}
          <a href={current.path} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-[#002FA7] hover:underline" data-testid="legal-view-live">
            <ExternalLink className="w-4 h-4" /> View live page
          </a>
        </div>

        {!doc ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="space-y-5">
            <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="legal-editor-card">
              <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-slate-500">
                <span className="px-2 py-0.5 rounded bg-slate-100 font-semibold text-slate-700" data-testid="legal-current-version">Live: v{doc.version}</span>
                <span>last published {doc.updated_at ? new Date(doc.updated_at).toLocaleString() : '—'} by {doc.updated_by_email}</span>
                {dirty && <span className="text-amber-700 font-semibold" data-testid="legal-unsaved">Unsaved changes</span>}
              </div>
              <div className="grid md:grid-cols-3 gap-3 mb-3">
                <label className="text-xs font-semibold text-slate-600 md:col-span-2">Title
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="legal-title-input"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-normal focus:outline-none focus:border-[#002FA7]" />
                </label>
                <label className="text-xs font-semibold text-slate-600">Effective date
                  <input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} data-testid="legal-effective-date-input"
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-normal focus:outline-none focus:border-[#002FA7]" />
                </label>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <button type="button" onClick={() => setPreview(false)} data-testid="legal-mode-edit" className={`px-3 py-1 rounded text-xs font-semibold ${!preview ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`}>Edit</button>
                <button type="button" onClick={() => setPreview(true)} data-testid="legal-mode-preview" className={`px-3 py-1 rounded text-xs font-semibold inline-flex items-center gap-1 ${preview ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700'}`}><Eye className="w-3 h-3" /> Preview</button>
                <span className="ml-auto text-xs text-slate-500">{form.body.length.toLocaleString()} characters</span>
              </div>
              {preview ? (
                <div className="border border-slate-200 rounded-md p-5 max-h-[60vh] overflow-auto bg-[#F8FAFC]" data-testid="legal-preview"><LegalMarkdown body={form.body} /></div>
              ) : (
                <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="legal-body-textarea" spellCheck
                  className="w-full min-h-[60vh] px-3 py-2 border border-slate-300 rounded-md text-sm font-mono leading-relaxed focus:outline-none focus:border-[#002FA7]" />
              )}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-4">
                <input value={form.change_note} onChange={(e) => setForm({ ...form, change_note: e.target.value })} placeholder="Change note for the version history (optional)" data-testid="legal-change-note-input"
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-[#002FA7]" />
                <button type="button" onClick={() => setForm({ title: doc.title, body: doc.body, effective_date: doc.effective_date, change_note: '' })} disabled={!dirty}
                  className="px-4 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40" data-testid="legal-discard">Discard</button>
                <button type="button" onClick={publish} disabled={!dirty || busy || form.body.trim().length < 50 || doc.slug !== slug} data-testid="legal-publish"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-md bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#00247D] disabled:opacity-50">
                  {busy === 'publish' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Publish v{doc.version + 1}
                </button>
              </div>
            </div>
            <AcceptanceStats stats={acceptances} onRemind={remind} busy={busy} />
            <VersionHistory slug={slug} versions={versions} onRestore={restore} onCompare={setCompare} busy={busy} />
          </div>
        )}
        {compare && doc && <LegalVersionDiff left={compare} right={doc} onClose={() => setCompare(null)} onRestore={restore} busy={busy} />}
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminLegal;
